// Original CSS/SVG text effects for EasyDesign (Canva-style, own implementation)

export function hexToRgba(hex, a = 1) {
  if (!hex || typeof hex !== "string") return `rgba(0,0,0,${a})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

export const EFFECTS = [
  { id: "none", label: "None" },
  { id: "shadow", label: "Shadow", color: true, intensity: true },
  { id: "lift", label: "Lift", intensity: true },
  { id: "glow", label: "Glow", color: true, intensity: true },
  { id: "echo", label: "Echo", color: true, intensity: true },
  { id: "outline", label: "Outline", color: true, intensity: true },
  { id: "hollow", label: "Hollow", intensity: true },
  { id: "splice", label: "Splice", color: true, intensity: true },
  { id: "neon", label: "Neon", color: true, intensity: true },
  { id: "glitch", label: "Glitch", intensity: true },
  { id: "background", label: "Background", color: true },
];

// Returns { style, mode, bgColor } — style merges onto the text node.
export function getTextEffectStyle(el, scale = 1) {
  const fsP = (el.fontSize || 40) * scale;
  const k = ((el.effectIntensity ?? 50) / 50) || 0.01;
  const ec = el.effectColor || "#000000";
  const fill = el.color || "#000000";
  const style = {};
  let mode = "plain";

  switch (el.effect) {
    case "shadow":
      style.textShadow = `${0.08 * fsP * k}px ${0.08 * fsP * k}px ${0.07 * fsP * k}px ${hexToRgba(ec, 0.45)}`;
      break;
    case "lift":
      style.textShadow = `0 ${0.05 * fsP}px ${0.13 * fsP * k}px rgba(0,0,0,0.42)`;
      break;
    case "glow":
      style.textShadow = `0 0 ${0.25 * fsP * k}px ${ec}, 0 0 ${0.5 * fsP * k}px ${ec}`;
      break;
    case "echo":
      style.textShadow = `${0.07 * fsP * k}px ${0.07 * fsP * k}px 0 ${hexToRgba(ec, 0.5)}, ${0.14 * fsP * k}px ${0.14 * fsP * k}px 0 ${hexToRgba(ec, 0.25)}`;
      break;
    case "outline":
      style.WebkitTextStroke = `${Math.max(1, 0.025 * fsP * k)}px ${ec}`;
      style.paintOrder = "stroke fill";
      break;
    case "hollow":
      style.color = "transparent";
      style.WebkitTextStroke = `${Math.max(1, 0.025 * fsP * k)}px ${fill}`;
      break;
    case "splice":
      style.color = "transparent";
      style.WebkitTextStroke = `${Math.max(1, 0.022 * fsP * k)}px ${fill}`;
      style.textShadow = `${0.07 * fsP * k}px ${0.07 * fsP * k}px 0 ${ec}`;
      style.paintOrder = "stroke fill";
      break;
    case "neon":
      style.color = "#ffffff";
      style.textShadow = `0 0 ${0.06 * fsP}px #fff, 0 0 ${0.2 * fsP * k}px ${ec}, 0 0 ${0.45 * fsP * k}px ${ec}, 0 0 ${0.75 * fsP * k}px ${ec}`;
      break;
    case "glitch":
      style.textShadow = `${0.045 * fsP * k}px 0 #ff00c1, -${0.045 * fsP * k}px 0 #00fff9`;
      break;
    case "background":
      mode = "background";
      break;
    default:
      break;
  }
  return { style, mode, bgColor: ec, fsP };
}

// Multi-stroke (layered text outline). el.strokes = [{ color, width }, ...]
// strokes[0] is the innermost (closest to the fill); each added stroke stacks outward.
// Returns layer styles ordered outermost-first (paint back-to-front), each a full
// copy of the text drawn with -webkit-text-stroke so concentric outlines appear.
export function strokeLayerStyles(el, scale = 1) {
  const list = Array.isArray(el.strokes)
    ? el.strokes.filter((s) => s && (s.width || 0) > 0)
    : [];
  if (!list.length) return [];
  let cum = 0;
  const layers = list.map((s) => {
    cum += Math.max(0, s.width || 0);
    return { width: cum, color: s.color || "#000000" };
  });
  // Outermost (largest cumulative width) painted first / behind.
  return layers.reverse().map((l) => ({
    WebkitTextStroke: `${l.width * 2 * scale}px ${l.color}`,
    color: "transparent",
    paintOrder: "stroke fill",
  }));
}

// Advanced themed presets (font + effect combos)
export const ADVANCED_PRESETS = [
  { id: "scifi", label: "Sci-fi", patch: { fontFamily: "Orbitron", fontWeight: "700", effect: "neon", effectColor: "#39d0ff", effectIntensity: 60, color: "#ffffff" } },
  { id: "screenprint", label: "Screenprint", patch: { fontFamily: "Staatliches", fontWeight: "400", effect: "echo", effectColor: "#111111", effectIntensity: 45, color: "#f4f1de" } },
  { id: "western", label: "Western", patch: { fontFamily: "Rye", fontWeight: "400", effect: "shadow", effectColor: "#2a1505", effectIntensity: 65, color: "#b4541f" } },
  { id: "graffiti", label: "Graffiti", patch: { fontFamily: "Permanent Marker", fontWeight: "400", effect: "outline", effectColor: "#000000", effectIntensity: 70, color: "#ff3ea5" } },
  { id: "bubble", label: "Bubble", patch: { fontFamily: "Fredoka", fontWeight: "600", effect: "outline", effectColor: "#ffffff", effectIntensity: 100, color: "#ff7ac6" } },
  { id: "aerobics", label: "Aerobics", patch: { fontFamily: "Audiowide", fontWeight: "400", effect: "echo", effectColor: "#ff2e9a", effectIntensity: 65, color: "#22d3ee" } },
  { id: "arcade", label: "Arcade", patch: { fontFamily: "Press Start 2P", fontWeight: "400", effect: "outline", effectColor: "#000000", effectIntensity: 55, color: "#ffd23f" } },
  { id: "cosmic", label: "Cosmic", patch: { fontFamily: "Monoton", fontWeight: "400", effect: "glow", effectColor: "#a855f7", effectIntensity: 75, color: "#e9d5ff" } },
  { id: "pixel", label: "Pixel", patch: { fontFamily: "VT323", fontWeight: "400", effect: "shadow", effectColor: "#000000", effectIntensity: 30, color: "#9be84f" } },
  { id: "3d", label: "3D", patch: { fontFamily: "Bungee", fontWeight: "400", effect: "echo", effectColor: "#000000", effectIntensity: 85, color: "#ffffff" } },
];
